import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import TakedownForm from "../components/TakedownForm";
import ShareCaseButtons from "../components/ShareCaseButtons";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Gavel,
  ExternalLink,
  PawPrint,
  FileWarning,
  ShieldAlert,
  ShieldCheck,
  Scale,
} from "lucide-react";
import { verificationTier, verificationMeta } from "../lib/verification";
import SEO, { abuserJsonLd } from "../components/SEO";
import StatusBadge from "../components/StatusBadge";
import PhotoUnavailablePlaceholder from "../components/PhotoUnavailablePlaceholder";

export default function AbuserDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [takedownOpen, setTakedownOpen] = useState(false);
  const [photoBroken, setPhotoBroken] = useState(false);

  useEffect(() => {
    api
      .get(`/abusers/${id}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        console.error(`Failed to load abuser ${id}:`, err);
        toast.error("Could not load this record — it may have been removed.");
        nav("/");
      })
      .finally(() => setLoading(false));
  }, [id, nav]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 label-eyebrow" data-testid="detail-loading">
        Loading record...
      </div>
    );
  }
  if (!data) return null;

  // Build per-case SEO + JSON-LD
  const seoTitle = `${data.name} — ${data.crime_type || "Animal Cruelty Conviction"} (${data.city || ""}, ${data.state_code || data.state || ""})`.replace(/, \)$/, ")").replace(/\( /, "(");
  const seoDesc = (
    data.crime_summary ||
    data.description ||
    `Court record for ${data.name} in ${data.city || ""}, ${data.state || ""}. Animal abuse conviction documented on Paws on Record.`
  ).slice(0, 160);
  const canonicalUrl = `https://pawsonrecord.com/abuser/${data.id}`;
  const ld = abuserJsonLd(data, canonicalUrl);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10" data-testid="detail-page">
      <SEO
        title={seoTitle}
        description={seoDesc}
        path={`/abuser/${data.id}`}
        image={data.photo_url || undefined}
        jsonLd={ld}
      />
      <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider hover:text-red-600" data-testid="detail-back">
        <ArrowLeft size={14} /> Back to Registry
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8">
        <div className="lg:col-span-8">
          {/* Prominent status badge for every record — near the name */}
          <div className="mb-4" data-testid="detail-status-badge-wrap">
            <StatusBadge disposition={data.disposition} size="md" />
          </div>

          {/* Pending case disclaimer — presumption of innocence */}
          {data.disposition?.category === "pending" && (
            <div
              className="mb-6 border-2 border-amber-500 bg-amber-50 p-4 flex items-start gap-3"
              data-testid="detail-pending-disclaimer"
            >
              <Scale size={20} className="text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900 leading-relaxed">
                <strong className="uppercase tracking-widest text-[11px] font-mono block mb-1">
                  Pending Case — Not a Conviction
                </strong>
                This case is pending. The individual has not been convicted of
                the offense described and is presumed innocent unless and until
                proven guilty. Information displayed is based on publicly
                available sources.
              </div>
            </div>
          )}

          {data.reoffender && (
            <div
              className="mb-6 border-2 border-red-600 bg-red-50 p-4 flex items-start gap-3"
              data-testid="detail-reoffender-banner"
            >
              <ShieldAlert size={20} className="text-red-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-900">
                <strong className="uppercase tracking-widest text-[11px] font-mono block mb-1">
                  Repeat Offender
                </strong>
                This individual has prior animal-cruelty convictions, a lifetime
                animal-ownership ban, or other recidivism indicators. Exercise
                extra caution when screening for adoption or foster placement.
              </div>
            </div>
          )}
          <div
            className="mb-6 border border-zinc-300 bg-zinc-50 p-3 flex items-center justify-between gap-3 text-xs"
            data-testid="detail-correction-banner"
          >
            <div className="flex items-center gap-2 text-zinc-700">
              <Scale size={14} className="text-zinc-500 flex-shrink-0" />
              <span>
                See an error in this record? We correct verified inaccuracies
                within 5 business days.
              </span>
            </div>
            <button
              type="button"
              onClick={() => setTakedownOpen(true)}
              className="font-semibold uppercase tracking-widest text-[10px] underline hover:text-red-600 flex-shrink-0"
              data-testid="detail-correction-banner-btn"
            >
              Request correction →
            </button>
          </div>
          <div className="border border-zinc-900">
            <div className="bg-zinc-900 text-white px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-red-600">
              <div>
                <div className="label-eyebrow text-red-500">{data.case_id}</div>
                <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1" data-testid="detail-name">
                  {data.name}
                </h1>
              </div>
              <ShareCaseButtons abuser={data} variant="dark" />
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <InfoTile icon={<Gavel size={16} />} label="Crime Type" value={data.crime_type} danger />
              <InfoTile icon={<MapPin size={16} />} label="Jurisdiction" value={`${data.city}, ${data.state}`} />
              <InfoTile icon={<Calendar size={16} />} label="Conviction Year" value={data.conviction_year} />
              <InfoTile icon={<PawPrint size={16} />} label="Animals Affected" value={data.animals_affected || "—"} />
            </div>
            {/* Photo on file — ALWAYS renders. Shows the verified booking/
                registry photo when we have one; otherwise the professional
                PAWS placeholder. If a stored URL breaks at load time, the
                onError handler flips photoBroken and the placeholder mounts
                in-place so the panel never renders as a blank gray box. */}
            <div className="p-6 border-t border-zinc-300" data-testid="detail-photo">
              <div className="label-eyebrow mb-3 flex items-center gap-2">
                <span>Photo on file</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 bg-zinc-50 text-zinc-600">
                  {data.disposition?.category === "pending"
                    ? "Pending case"
                    : data.disposition?.category === "needs_review"
                      ? "Under review"
                      : "Convicted record"}
                </span>
              </div>
              {data.photo_url && !photoBroken ? (
                <figure className="max-w-sm" data-testid="detail-photo-real">
                  <img
                    src={data.photo_url}
                    alt={`${data.name} — booking/registry photo`}
                    className="w-full border border-zinc-900 grayscale-[20%]"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={() => setPhotoBroken(true)}
                    onLoad={(e) => {
                      // Some hotlink-blockers return a 1x1 pixel with HTTP 200.
                      // Treat anything under 40px as broken and swap to the placeholder.
                      const img = e.currentTarget;
                      if ((img.naturalWidth || 0) < 40 || (img.naturalHeight || 0) < 40) {
                        setPhotoBroken(true);
                      }
                    }}
                  />
                  {data.photo_source && (
                    <figcaption className="text-[11px] uppercase tracking-widest font-mono text-zinc-500 mt-2">
                      Photo: {data.photo_source}
                    </figcaption>
                  )}
                </figure>
              ) : (
                <div data-testid="detail-photo-placeholder">
                  <PhotoUnavailablePlaceholder
                    animals={data.animals_affected}
                    size="lg"
                  />
                  <div className="text-[11px] uppercase tracking-widest font-mono text-zinc-500 mt-2 max-w-sm leading-relaxed">
                    No verified offender photograph on file. Paws on Record
                    does not use stock, AI-generated, or unverified images.
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-zinc-300">
              <div className="label-eyebrow mb-2">Case Summary</div>
              <p className="text-[17px] leading-relaxed" data-testid="detail-summary">
                {data.crime_summary}
              </p>
            </div>
            <div className="p-6 border-t border-zinc-300">
              <div className="label-eyebrow mb-2">Details</div>
              <p className="text-base leading-relaxed text-zinc-700">
                {data.description}
              </p>
            </div>
            <div className="p-6 border-t border-zinc-300">
              <div className="label-eyebrow mb-2">Sentencing</div>
              <p className="text-base leading-relaxed font-semibold">
                {data.sentence}
              </p>
            </div>
            {data.source_url && (
              <div className="p-6 border-t border-zinc-300">
                <div className="label-eyebrow mb-2 flex items-center gap-2">
                  Source
                  {(() => {
                    const tier = verificationTier(data);
                    const v = verificationMeta(tier);
                    return (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border ${v.cls}`}
                        title={v.description}
                        data-testid={`detail-verified-${tier}`}
                      >
                        {tier === "official" ? <ShieldCheck size={10} /> : <FileWarning size={10} />}
                        {v.label}
                      </span>
                    );
                  })()}
                </div>
                <a
                  href={data.source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 font-semibold hover:underline"
                >
                  {data.source_label || "Source record"} <ExternalLink size={14} />
                </a>
                <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
                  {verificationMeta(verificationTier(data)).description}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <div className="border border-red-600 bg-red-50 p-6">
            <div className="label-eyebrow text-red-700">If you witness abuse</div>
            <h3 className="font-display font-black text-2xl tracking-tight mt-2 text-red-700">
              Take action now.
            </h3>
            <p className="text-sm mt-3 text-red-900">
              Do not confront the abuser. Document what you can from a safe
              distance, then report to authorities.
            </p>
            <div className="mt-5 flex flex-col gap-2">
              <Link to="/report" className="btn-danger justify-center w-full" data-testid="detail-report-cta">
                <FileWarning size={14} /> Submit a Report
              </Link>
              <Link to="/nearby" className="btn-outline justify-center w-full border-red-600 text-red-700 hover:bg-red-600 hover:text-white" data-testid="detail-nearby-cta">
                <MapPin size={14} /> Find Help Nearby
              </Link>
            </div>
          </div>

          <div className="border border-zinc-900 p-6">
            <div className="label-eyebrow">Shelter & rescue advisory</div>
            <p className="text-sm mt-3 text-zinc-700 leading-relaxed">
              Shelters, rescues and fostering organizations are encouraged to
              cross-reference this registry when vetting prospective adopters
              or fosters in <span className="font-bold">{data.state}</span>.
            </p>
          </div>

          {data.aliases && data.aliases.length > 0 && (
            <div className="border border-zinc-300 p-6">
              <div className="label-eyebrow mb-3">Known Aliases</div>
              <div className="flex flex-wrap gap-2">
                {data.aliases.map((a) => (
                  <span key={a} className="badge-neutral">{a}</span>
                ))}
              </div>
            </div>
          )}

          {/* Correction / Takedown CTA (legal-risk reduction) */}
          <div className="border border-zinc-900 p-5 bg-zinc-50" data-testid="detail-takedown-cta">
            <div className="label-eyebrow flex items-center gap-2">
              <Scale size={12} /> Is this record inaccurate?
            </div>
            <p className="text-sm text-zinc-700 mt-2 leading-relaxed">
              If you are the subject of this record, their attorney, or have
              authoritative evidence of an error (conviction overturned,
              expunged, or mis-identified individual), you can request a
              correction or takedown.
            </p>
            <button
              type="button"
              className="btn-outline mt-4 w-full justify-center"
              onClick={() => setTakedownOpen(true)}
              data-testid="detail-takedown-btn"
            >
              <Scale size={14} /> Request Correction / Takedown
            </button>
          </div>

          <div className="border border-zinc-300 p-5 bg-zinc-50">
            <div className="label-eyebrow">Status</div>
            <div className="font-display font-black text-xl mt-1">{data.status}</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-2">
              PUBLIC RECORD · VERIFY WITH LINKED SOURCE
            </div>
          </div>
        </div>
      </div>
      {takedownOpen && (
        <TakedownForm
          abuserId={data.id}
          abuserName={data.name}
          onClose={() => setTakedownOpen(false)}
        />
      )}
    </div>
  );
}

function InfoTile({ icon, label, value, danger }) {
  return (
    <div
      className={`border p-4 ${
        danger ? "border-red-300 bg-red-50" : "border-zinc-300 bg-white"
      }`}
    >
      <div className="flex items-center gap-2 label-eyebrow">
        {icon}
        {label}
      </div>
      <div className="mt-2 font-display font-bold text-lg leading-tight">
        {value}
      </div>
    </div>
  );
}
